// src/encounter/invoice-block.tsx
// The Wrap-up Invoice block drawn as an invoice (Antonin, 19 Oct: "make the invoice look more like an invoice"). The same data
// (`InvoiceSectionData`) and the same actions as the DS `WrapUpInvoicePanel` it replaces for a SELF-PAY invoice:
//   header     tile + "Invoice" + the two block actions (Add service, Preview invoice: neutral secondary)
//   meta line  invoice number · date · status chip (only the parts the invoice has)
//   Diagnosis  the invoice's diagnoses
//   lines      Service (name, group) · Code (CPT / internal) · Qty · Gross (the DS panel's own words for the line's gross); a
//              hairline between rows; edit / remove
//              as the 32 neutral icon buttons, shown on row hover or focus
//   totals     right-aligned, in the Practice app's words and order: Gross Total, Discount Total, Net Total (always, even at a
//              zero discount), patient / sponsor share rows if present, Management Discount (self-pay, when set), Patient
//              Payable (Insurance and Corporate only, as PM `payment-section.tsx:262-270`), Deposited and Outstanding. The
//              emphasised total under a hairline is Patient Payable, or Outstanding for self-pay. The management-discount
//              edit sits on Net Total (`invoiceTotalRows` in lib/to-invoice-section.ts).
// Gaps (no source on the invoice data): no per-unit price field, so no Unit price column (it would be invented); no line-level
// tax; the sponsored variant (coverage, auth code, validity, service date) stays on the DS panel (see wrap-up-invoice-section.tsx).
import { Eye, Pencil, Plus, Receipt, Trash2 } from 'lucide-react';
import { Button, StatusChip, type StatusChipProps } from '@scripsteam/scrips-signal-ds';
import { formatDateD45 } from '@/shared/lib/date-format';
import { formatMoney as formatPlain } from '@/shared/lib/money';
import { formatMoney, invoiceTotalRows, type InvoiceSectionData } from './lib/to-invoice-section';

type Semantic = NonNullable<StatusChipProps['semantic']>;
const STATUS_SEMANTIC: Record<string, Semantic> = { Issued: 'info', Draft: 'neutral', Cancelled: 'error', Submitted: 'success' };

export interface InvoiceBlockProps {
  data: InvoiceSectionData | null;
  onAddService?: () => void;
  onPreviewInvoice?: () => void;
  onRemoveService?: (lineId: string) => void;
  onEditService?: (lineId: string) => void;
  busy?: boolean;
  onEditManagementDiscount?: () => void;
  /** Inside a section card: no tile or title (the card's block draws them); see InvoiceActions for the two buttons. */
  embedded?: boolean;
}

/** The invoice's two block actions (Add service, Preview invoice), for a card block's action slot. */
export function InvoiceActions({ data, onAddService, onPreviewInvoice, busy }: Pick<InvoiceBlockProps, 'data' | 'onAddService' | 'onPreviewInvoice' | 'busy'>) {
  return (
    <>
      {data?.draft && onAddService ? (
        <Button type="button" variant="tertiary" size="sm" icon={<Plus aria-hidden />} disabled={busy} onClick={onAddService}>
          Add service
        </Button>
      ) : null}
      {data && onPreviewInvoice ? (
        <Button type="button" variant="tertiary" size="sm" icon={<Eye aria-hidden />} disabled={busy} onClick={onPreviewInvoice}>
          Preview invoice
        </Button>
      ) : null}
    </>
  );
}

export function InvoiceBlock({ data, onAddService, onPreviewInvoice, onRemoveService, onEditService, busy, onEditManagementDiscount, embedded }: InvoiceBlockProps) {
  const currency = data?.currencyCode ?? 'AED';
  const meta = data?.meta;
  const metaParts = [meta?.number, meta?.date ? formatDateD45(meta.date) : undefined].filter(Boolean);
  return (
    <section aria-label="Invoice" data-organism="wrap-up-invoice-panel" data-variant="invoice" data-embedded={embedded ? 'true' : undefined}>
      {embedded ? null : (
      <div>
        <span aria-hidden="true">
          <Receipt aria-hidden />
        </span>
        <h3>Invoice</h3>
        <div>
          {data?.draft && onAddService ? (
            <Button type="button" variant="secondary" size="sm" icon={<Plus aria-hidden />} disabled={busy} onClick={onAddService}>
              Add service
            </Button>
          ) : null}
          {data && onPreviewInvoice ? (
            <Button type="button" variant="secondary" size="sm" icon={<Eye aria-hidden />} disabled={busy} onClick={onPreviewInvoice}>
              Preview invoice
            </Button>
          ) : null}
        </div>
      </div>
      )}
      {!data ? (
        <p data-slot="invoice-empty">Invoice not created</p>
      ) : (
        <>
          {metaParts.length > 0 || meta?.status ? (
            <p data-slot="invoice-meta">
              {metaParts.join(' · ')}
              {meta?.status ? (
                <StatusChip semantic={STATUS_SEMANTIC[meta.status] ?? 'neutral'} tone="soft">
                  {meta.status}
                </StatusChip>
              ) : null}
            </p>
          ) : null}
          <div data-slot="invoice-dx">
            <p>Diagnosis</p>
            {data.dx.length === 0 ? (
              <p>No diagnosis available</p>
            ) : (
              <ol>
                {data.dx.map((d) => (
                  <li key={d.index} title={d.title}>
                    <span>{d.index}</span>
                    <span>{d.title}</span>
                    {d.description ? <span>{d.description}</span> : null}
                  </li>
                ))}
              </ol>
            )}
          </div>
          {data.rows.length === 0 ? (
            <p data-slot="invoice-empty">{data.draft ? 'No services added. Use Add service.' : 'No services on this invoice.'}</p>
          ) : (
            <table data-slot="invoice-lines">
              <caption>Invoice services</caption>
              <thead>
                <tr>
                  <th scope="col">Service</th>
                  <th scope="col">Code</th>
                  <th scope="col">Qty</th>
                  <th scope="col">Gross, {currency}</th>
                  <th scope="col">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <span>{r.label}</span>
                      {r.groupLine ? <span>{r.groupLine}</span> : null}
                    </td>
                    <td>{r.codeLine}</td>
                    <td>{r.quantity}</td>
                    <td>{formatPlain(r.gross)}</td>
                    <td>
                      {onEditService ? (
                        <button type="button" className="icon-action" aria-label={`Edit ${r.label}`} disabled={busy} onClick={() => onEditService(r.id)}>
                          <Pencil aria-hidden />
                        </button>
                      ) : null}
                      {onRemoveService ? (
                        <button type="button" className="icon-action" aria-label={`Remove ${r.label}`} disabled={busy} onClick={() => onRemoveService(r.id)}>
                          <Trash2 aria-hidden />
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <dl data-slot="invoice-totals">
            {invoiceTotalRows(data).map((t) => {
              // The management discount is edited on Net Total, so a self-pay invoice (no Patient Payable row) still offers it.
              // Self-pay only, as before: a sponsored invoice has no management discount.
              const editable = !!onEditManagementDiscount && !data.sponsored && t.label === 'Net Total';
              return (
                <div key={t.label} data-total={t.emphasis ? 'payable' : undefined}>
                  <dt>{t.label}</dt>
                  <dd>
                    {editable ? (
                      <button
                        type="button"
                        aria-label={`Edit management discount, ${t.label} ${formatMoney(t.amount, currency)}`}
                        title="Edit management discount"
                        disabled={busy}
                        onClick={onEditManagementDiscount}
                      >
                        {formatMoney(t.amount, currency)}
                      </button>
                    ) : (
                      formatMoney(t.amount, currency)
                    )}
                  </dd>
                </div>
              );
            })}
          </dl>
        </>
      )}
    </section>
  );
}
